import { NetworkStatus } from "@apollo/client";
import { routes } from "@constants/routes";
import { JobPositionsAssignmentsList, UserTypeEnum } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountJobPositionAssignmentStatusChoices,
  JobPositionAssignmentNode,
  JobSeekerType,
  useGetAssignmentsQuery,
} from "@graphql/generated";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface IFilterFormData {
  name?: string;
  score?: {
    value: string;
  };
  date?: string;
  status?: {
    value: AuthAccountJobPositionAssignmentStatusChoices | "all";
  };
}

const ResumesTabContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [currentFilters, setCurrentFilters] = useState<{
    jobSeekerFullName?: string;
    jobSeekerProfileScoreGte?: number;
    createdAtGte?: string;
    jobSeekerStatus?: AuthAccountJobPositionAssignmentStatusChoices;
  }>({});

  const { data, loading, refetch, networkStatus } = useGetAssignmentsQuery({
    variables: {
      id: id || "",
      offset: (page - 1) * pageSize,
      first: pageSize,
      jobSeekerFullName: currentFilters.jobSeekerFullName,
      jobSeekerProfileScoreGte: currentFilters.jobSeekerProfileScoreGte,
      createdAtGte: currentFilters.createdAtGte,
      statusIn: currentFilters.jobSeekerStatus ? [currentFilters.jobSeekerStatus] : undefined,
    },
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  const assignments = (data?.admin?.jobPositions?.edges[0]?.node?.assignments?.edges
    ?.map((edge) => edge?.node)
    .filter(Boolean) || []) as any[];

  const navigateToViewJobSeeker = (assignment: JobPositionAssignmentNode) => {
    if (!(assignment?.jobSeeker as JobSeekerType)?.id) return;

    navigate(routes.talentPool.detail.replace(":id", (assignment?.jobSeeker as JobSeekerType)?.id));
  };

  const handleFilterChange = (filters: IFilterFormData) => {
    setPage(1);
    const newFilters = {
      jobSeekerFullName: filters.name,
      jobSeekerProfileScoreGte: filters.score?.value ? parseInt(filters.score?.value) : undefined,
      createdAtGte: filters.date,
      jobSeekerStatus: filters.status?.value !== "all" ? filters.status?.value : undefined,
    };
    setCurrentFilters(newFilters);
    refetch({
      id: id || "",
      offset: 0,
      first: pageSize,
      ...newFilters,
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    refetch({
      id: id || "",
      offset: (newPage - 1) * pageSize,
      first: pageSize,
      jobSeekerFullName: currentFilters.jobSeekerFullName,
      jobSeekerProfileScoreGte: currentFilters.jobSeekerProfileScoreGte,
      createdAtGte: currentFilters.createdAtGte,
      statusIn: currentFilters.jobSeekerStatus ? [currentFilters.jobSeekerStatus] : undefined,
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPage(1);
    setPageSize(newPageSize);
    refetch({
      id: id || "",
      offset: 0,
      first: newPageSize,
      jobSeekerFullName: currentFilters.jobSeekerFullName,
      jobSeekerProfileScoreGte: currentFilters.jobSeekerProfileScoreGte,
      createdAtGte: currentFilters.createdAtGte,
      statusIn: currentFilters.jobSeekerStatus ? [currentFilters.jobSeekerStatus] : undefined,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <JobPositionsAssignmentsList
        assignments={assignments}
        onClickAssignmentCard={navigateToViewJobSeeker}
        onFilterChange={handleFilterChange}
        total={data?.admin?.jobPositions?.edges[0]?.node?.assignments?.totalCount || 0}
        page={page}
        pageSize={pageSize}
        handlePageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        loading={loading || networkStatus === NetworkStatus.refetch}
        userType={UserTypeEnum.ADMIN}
      />
    </div>
  );
};

export default ResumesTabContent;
